/**
 * UserSync Hook - Bridges Clerk authentication with backend database
 * 
 * IMPORTANT: This hook must ONLY be called when ClerkProvider is wrapping the app.
 * For apps without Clerk, use useBackendUserFallback instead.
 */

import { useEffect, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';

// API base URL configuration
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    return '/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Custom hook to sync Clerk user with backend database
 * MUST be used inside ClerkProvider
 */
export const useBackendUser = () => {
    const { user: clerkUser, isLoaded: isClerkLoaded, isSignedIn } = useUser();

    const [backendUser, setBackendUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [syncError, setSyncError] = useState(null);
    const [hasSynced, setHasSynced] = useState(false);

    const generateBackendPassword = useCallback((clerkUserId) => {
        return `clerk_${clerkUserId}_secure`;
    }, []);

    const tryBackendLogin = useCallback(async (email, password, clerk_id = null, image_url = null) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, clerk_id, image_url })
            });
            return await response.json();
        } catch (error) {
            console.error('Backend login failed:', error);
            return { success: false, error: error.message };
        }
    }, []);

    const createBackendUser = useCallback(async (userData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData)
            });
            return await response.json();
        } catch (error) {
            console.error('Backend registration failed:', error);
            return { success: false, error: error.message };
        }
    }, []);

    const extractClerkUserInfo = useCallback((clerk) => {
        const email = clerk.primaryEmailAddress?.emailAddress || '';
        const fullName = clerk.fullName ||
            `${clerk.firstName || ''} ${clerk.lastName || ''}`.trim() ||
            email.split('@')[0] ||
            'User';

        // Extract metadata fields
        const metadata = clerk.unsafeMetadata || {};
        const dateOfBirth = metadata.date_of_birth || metadata.dob || null;
        const gender = metadata.gender || 'Not specified';
        const occupation = metadata.occupation || 'Not specified';
        const imageUrl = clerk.imageUrl || null;

        return { email, fullName, dateOfBirth, gender, occupation, imageUrl };
    }, []);

    // Helper to calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob) return null;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Helper to get age range
    const getAgeRange = (age) => {
        if (!age) return '20-29'; // Default
        if (age < 18) return 'Under 18';
        if (age <= 24) return '18-24';
        if (age <= 34) return '25-34';
        if (age <= 44) return '35-44';
        if (age <= 54) return '45-54';
        if (age <= 64) return '55-64';
        return '65+';
    };

    // Helper for zodiac
    const calculateZodiacSign = (dateString) => {
        if (!dateString) return 'unknown';
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.getMonth() + 1;

        if ((month === 1 && day <= 19) || (month === 12 && day >= 22)) return 'Capricorn';
        if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'Aquarius';
        if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return 'Pisces';
        if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'Aries';
        if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'Taurus';
        if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'Gemini';
        if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'Cancer';
        if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Leo';
        if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Virgo';
        if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'Libra';
        if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'Scorpio';
        if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'Sagittarius';
        return 'unknown';
    };

    const syncUserWithBackend = useCallback(async () => {
        if (!isClerkLoaded) return;

        if (!isSignedIn || !clerkUser) {
            setBackendUser(null);
            setIsLoading(false);
            setHasSynced(true);
            return;
        }

        if (hasSynced && backendUser) {
            setIsLoading(false);
            return;
        }

        // Check sessionStorage cache — keyed by Clerk user ID to prevent cross-user leakage
        const cacheKey = `backendUser_${clerkUser.id}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const cachedUser = JSON.parse(cached);
                setBackendUser(cachedUser);
                setHasSynced(true);
                setIsLoading(false);
                return;
            }
        } catch {}

        setIsLoading(true);
        setSyncError(null);

        try {
            const { email, fullName, dateOfBirth, gender, occupation, imageUrl } = extractClerkUserInfo(clerkUser);
            const password = generateBackendPassword(clerkUser.id);

            // Calculate derived fields
            const age = calculateAge(dateOfBirth);
            const ageRange = getAgeRange(age);
            const zodiacSign = calculateZodiacSign(dateOfBirth);

            // Pass clerk_id during login so backend can link it if missing
            const loginResult = await tryBackendLogin(email, password, clerkUser.id, imageUrl);

            if (loginResult.success) {
                console.log('✅ Backend user found:', loginResult.user.name);
                // Merge backend data with Clerk metadata in case it's fresher or missing in DB
                const userData = {
                    ...loginResult.user,
                    dateOfBirth,
                    gender: loginResult.user.gender || gender
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(userData));
                setBackendUser(userData);
                setHasSynced(true);
            } else {
                console.log('📝 Creating new backend user for:', email);

                const registrationData = {
                    name: fullName,
                    email: email,
                    occupation: occupation,
                    ageRange: ageRange,
                    zodiacSign: zodiacSign,
                    password: password,
                    confirmPassword: password,
                    clerk_id: clerkUser.id,
                    image_url: imageUrl
                };

                const registerResult = await createBackendUser(registrationData);

                if (registerResult.success) {
                    console.log('✅ New backend user created:', registerResult.user.name);
                    const newUserData = {
                        ...registerResult.user,
                        dateOfBirth,
                        gender
                    };
                    sessionStorage.setItem(cacheKey, JSON.stringify(newUserData));
                    setBackendUser(newUserData);
                    setHasSynced(true);
                } else {
                    console.warn('⚠️ Backend registration failed:', registerResult.error);
                    setSyncError(registerResult.error);
                    setBackendUser({
                        id: clerkUser.id,
                        name: fullName,
                        email: email,
                        dateOfBirth,
                        gender,
                        occupation,
                        isClerkOnly: true
                    });
                    setHasSynced(true);
                }
            }
        } catch (error) {
            console.error('❌ User sync error:', error);
            setSyncError(error.message);
            const { email, fullName, dateOfBirth, gender, occupation, imageUrl } = extractClerkUserInfo(clerkUser);
            setBackendUser({
                id: clerkUser.id,
                name: fullName,
                email: email,
                dateOfBirth,
                gender,
                occupation,
                imageUrl,
                isClerkOnly: true
            });
            setHasSynced(true);
        } finally {
            setIsLoading(false);
        }
    }, [
        isClerkLoaded,
        isSignedIn,
        clerkUser,
        hasSynced,
        backendUser,
        extractClerkUserInfo,
        generateBackendPassword,
        tryBackendLogin,
        createBackendUser
    ]);

    useEffect(() => {
        syncUserWithBackend();
    }, [syncUserWithBackend]);

    useEffect(() => {
        if (!isSignedIn) {
            setHasSynced(false);
            setBackendUser(null);
            // Clear all cached user data on sign-out to prevent cross-user leakage
            Object.keys(sessionStorage)
                .filter(k => k.startsWith('backendUser_'))
                .forEach(k => sessionStorage.removeItem(k));
        }
    }, [isSignedIn]);

    return {
        backendUser,
        isLoading,
        clerkUser,
        isSignedIn,
        syncError,
        isClerkAvailable: isClerkLoaded
    };
};

/**
 * Fallback hook for when Clerk is NOT configured
 * This is a plain function that returns static data (not a real hook)
 */
export const useBackendUserFallback = () => {
    return {
        backendUser: null,
        isLoading: false,
        clerkUser: null,
        isSignedIn: false,
        syncError: null,
        isClerkAvailable: false
    };
};

// Check if Clerk is configured
const isClerkConfigured = Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);

export const useClerkAvailability = () => {
    return { isClerkConfigured };
};

export default useBackendUser;
